import React from 'react';
import {
  Typography,
  Box,
  Button,
  IconButton,
  Paper,
  Card,
  CardContent,
  Stack,
  Chip
} from '@mui/material';
import { 
  Visibility as VisibilityIcon,
  Lightbulb as LightbulbIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import DataTable, { ColumnDefinition } from '../common/DataTable';
import { FancySkeleton } from '../common/FancySkeleton';
import { Idea, SortConfig } from '../../hooks/useIdeas';

interface IdeaCardProps {
  idea: Idea;
  onView: (id: string) => void;
}

// Mobile Card Component for individual ideas
const IdeaCard: React.FC<IdeaCardProps> = ({ idea, onView }) => {
  // Get creator name
  let creatorName = 'Unknown';
  if (idea.user) {
    if (idea.user.firstName && idea.user.lastName) {
      creatorName = `${idea.user.firstName} ${idea.user.lastName}`;
    } else if (idea.user.firstName) {
      creatorName = idea.user.firstName;
    } else if (idea.user.lastName) {
      creatorName = idea.user.lastName;
    } else if (idea.user.email) {
      creatorName = idea.user.email;
    }
  }

  return (
    <Card 
      sx={{ 
        mb: 2, 
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 2
        }
      }}
      onClick={() => onView(idea.id)}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 1 }}>
          <Typography 
            variant="h6" 
            component="h3" 
            sx={{ 
              fontWeight: 'bold',
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.2,
              maxHeight: '2.4em'
            }}
            title={idea.topic} // Show full text on tap/hover
          >
            {idea.topic}
          </Typography>
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              onView(idea.id);
            }}
            sx={{ flexShrink: 0 }}
          >
            <VisibilityIcon />
          </IconButton>
        </Box>
        
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Account:</Typography>
            <Typography variant="body2">{idea.account || 'Unknown'}</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Sector:</Typography>
            <Typography variant="body2">{idea.sector || 'Unknown'}</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Platform:</Typography>
            <Typography variant="body2">{idea.platform || 'Unknown'}</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Persona:</Typography>
            <Typography variant="body2">{idea.icp.title || 'Unknown'}</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Created by:</Typography>
            <Typography variant="body2">{creatorName}</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Created:</Typography>
            <Typography variant="body2">{new Date(idea.createdAt).toLocaleDateString()}</Typography>
          </Box>
        </Stack>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Projected Impact:</Typography>
            {(idea.isProcessing || (idea.projectedImpact.level === 'Unknown' && idea.projectedImpact.score <= 0)) ? (
              <FancySkeleton height={18} width={120} radius={10} />
            ) : (
              <Chip 
                label={idea.projectedImpact.level}
                size="small"
                sx={{ backgroundColor: idea.projectedImpact.color + '20', color: idea.projectedImpact.color, fontWeight: 'medium' }}
              />
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

interface IdeasListProps {
  ideas: Idea[];
  sortConfig: SortConfig;
  onSort: (key: string) => void;
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onNewIdea: () => void;
}

const IdeasList: React.FC<IdeasListProps> = ({
  ideas,
  sortConfig,
  onSort,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  onNewIdea
}) => {
  const navigate = useNavigate();

  // Handle row click to navigate to idea details
  const handleRowClick = (idea: Idea) => {
    navigate(`/ideas/${idea.id}/results`);
  };

  // Column configuration for DataTable
  const columns: ColumnDefinition<Idea>[] = [
    {
      key: 'account',
      label: 'Account',
      render: (idea) => (
        <Typography sx={{ fontWeight: 'medium' }}>
          {idea.account}
        </Typography>
      )
    },
    {
      key: 'sector',
      label: 'Sector',
      render: (idea) => <Typography>{idea.sector}</Typography>
    },
    {
      key: 'platform',
      label: 'Platform',
      render: (idea) => <Typography>{idea.platform}</Typography>
    },
    {
      key: 'topic',
      label: 'Topic',
      render: (idea) => (
        <Typography 
          sx={{ 
            fontWeight: 'medium',
            maxWidth: '300px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={idea.topic} // Show full text on hover
        >
          {idea.topic}
        </Typography>
      )
    },
    {
      key: 'icp',
      label: 'Persona',
      render: (idea) => <Typography>{idea.icp.title}</Typography>
    },
    {
      key: 'createdBy',
      label: 'Created By',
      sortable: false,
      render: (idea) => {
        let creatorName = 'Unknown';
        if (idea.user) {
          if (idea.user.firstName && idea.user.lastName) {
            creatorName = `${idea.user.firstName} ${idea.user.lastName}`;
          } else if (idea.user.firstName) {
            creatorName = idea.user.firstName;
          } else if (idea.user.lastName) {
            creatorName = idea.user.lastName;
          } else if (idea.user.email) {
            creatorName = idea.user.email;
          }
        }
        return <Typography>{creatorName}</Typography>;
      }
    },
    {
      key: 'createdAt',
      label: 'Created Date',
      render: (idea) => <Typography>{new Date(idea.createdAt).toLocaleString()}</Typography>
    },
    {
      key: 'projectedImpact',
      label: 'Projected Impact',
      sortKey: 'projectedImpact.score',
      render: (idea) => {
        const isLoading = idea.isProcessing || (idea.projectedImpact.level === 'Unknown' && idea.projectedImpact.score <= 0);
        if (isLoading) return <FancySkeleton height={18} width={140} radius={10} />;
        return (
          <Typography sx={{ color: idea.projectedImpact.color, fontWeight: 'medium' }}>
            {Math.round(idea.projectedImpact.score)} • {idea.projectedImpact.level}
          </Typography>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      render: (idea) => (
        <IconButton 
          aria-label="view idea" 
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click event
            navigate(`/ideas/${idea.id}/results`);
          }}
          size="small"
        >
          <VisibilityIcon />
        </IconButton>
      )
    }
  ];

  // Mobile sort options
  const mobileSortOptions = [
    { key: 'createdAt', label: 'Created Date' },
    { key: 'topic', label: 'Topic' },
    { key: 'account', label: 'Account' },
    { key: 'sector', label: 'Sector' },
    { key: 'platform', label: 'Platform' },
    { key: 'icp', label: 'Persona' },
    { key: 'projectedImpact.score', label: 'Projected Impact' }
  ];

  // Mobile card renderer
  const renderMobileCard = (idea: Idea) => (
    <IdeaCard
      key={idea.id}
      idea={idea}
      onView={(id) => navigate(`/ideas/${id}/results`)}
    />
  );

  // Empty state component for when there are no ideas
  const emptyStateComponent = (
    <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paper' }}>
      <LightbulbIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'medium' }}>
        No Ideas Yet
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
        Get started by generating your first set of content ideas. Our AI will help you brainstorm 
        engaging posts tailored to your brand and audience.
      </Typography>
      <Button
        variant="contained"
        color="primary"
        size="large"
        startIcon={<LightbulbIcon />}
        onClick={onNewIdea}
        sx={{ mt: 1 }}
      >
        Generate Your First Ideas
      </Button>
    </Paper>
  );

  return (
    <Box component="section" aria-labelledby="ideas-content-heading">
      <Typography 
        component="h2" 
        id="ideas-content-heading"
        sx={{ 
          border: 0,
          clip: 'rect(0 0 0 0)',
          height: '1px',
          margin: '-1px',
          overflow: 'hidden',
          padding: 0,
          position: 'absolute',
          width: '1px',
          whiteSpace: 'nowrap', 
        }}
      >
        Ideas List
      </Typography>

      <DataTable
        data={ideas}
        columns={columns}
        sortConfig={sortConfig}
        onSort={onSort}
        onRowClick={handleRowClick}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        renderMobileCard={renderMobileCard}
        mobileSortOptions={mobileSortOptions}
        emptyStateComponent={emptyStateComponent}
        tableAriaLabel="ideas table"
      />
    </Box>
  );
};

export default IdeasList;
